// MUNYA OS · Skeleton loaders · Workstream D6 + Y6
//
// Skeletons "por forma exacta" — cada vista usa su propia composición. Estos
// son los building blocks + 5 composiciones canónicas reutilizables (Table,
// Cards, Kanban, FichaProyecto, ListaLeads).
//
// Animación shimmer · CLS=0 garantizado · `prefers-reduced-motion` honra automáticamente
// (definido en tokens.css).

import type { ReactElement } from 'react';

export function Skeleton({ className }: { className?: string }): ReactElement {
  return <div className={`skeleton-shimmer rounded ${className ?? ''}`} aria-hidden="true" />;
}

export function SkeletonText({
  lines = 1,
  className = '',
}: {
  lines?: number;
  className?: string;
}): ReactElement {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-3 ${i === lines - 1 ? 'w-2/3' : 'w-full'}`} />
      ))}
    </div>
  );
}

export function SkeletonTable({
  rows = 5,
  cols = 4,
}: {
  rows?: number;
  cols?: number;
}): ReactElement {
  return (
    <div className="space-y-2" role="status" aria-label="Cargando">
      <div className="flex gap-2 bg-[var(--color-munya-sand)]/30 rounded px-2 py-2">
        {Array.from({ length: cols }).map((_, j) => (
          <Skeleton key={j} className="h-4 flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-2 px-2 py-2 border-b border-[var(--color-munya-sand)]/30">
          {Array.from({ length: cols }).map((_, j) => (
            <Skeleton key={j} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 6 }: { count?: number }): ReactElement {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
      role="status"
      aria-label="Cargando"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="p-4 rounded-lg border border-[var(--color-munya-sand)] bg-[var(--color-munya-surface)]/30 space-y-3"
        >
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-2/3" />
          <div className="flex gap-1 pt-2">
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-6 w-12" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton específico del kanban TRACK / CRM · 4 columnas con N cards cada una.
 * Refleja exactamente la forma del LeadBoard / TrackTab para CLS=0.
 */
export function SkeletonKanban({
  columns = 4,
  cardsPerColumn = 3,
}: {
  columns?: number;
  cardsPerColumn?: number;
}): ReactElement {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" role="status" aria-label="Cargando kanban">
      {Array.from({ length: columns }).map((_, c) => (
        <div
          key={c}
          className="flex flex-col gap-2 w-72 shrink-0 bg-[var(--color-munya-sand)]/20 rounded-lg p-3"
        >
          <div className="flex items-center justify-between mb-1">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-6 rounded-full" />
          </div>
          {Array.from({ length: cardsPerColumn }).map((_, i) => (
            <div
              key={i}
              className="bg-[var(--color-munya-surface)] rounded p-3 space-y-2 border border-[var(--color-munya-sand)]/60"
            >
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
              <div className="flex gap-1.5 pt-1">
                <Skeleton className="h-5 w-12 rounded-full" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton ficha de proyecto · header + tabs + bloque principal.
 * Refleja la forma del ProjectDetail.tsx (11 pestañas).
 */
export function SkeletonProjectDetail(): ReactElement {
  return (
    <div className="space-y-5" role="status" aria-label="Cargando proyecto">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-64" />
          <div className="flex gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-9 rounded" />
          <Skeleton className="h-9 w-24 rounded" />
        </div>
      </div>
      <div className="flex gap-1 border-b border-[var(--color-munya-sand)] pb-2 overflow-x-auto">
        {Array.from({ length: 11 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-20 shrink-0" />
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="p-4 rounded border border-[var(--color-munya-sand)] bg-[var(--color-munya-surface)]/40 space-y-2"
          >
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-6 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton lista de leads · 1 KPIs cards header + filtros + tabla compacta.
 */
export function SkeletonLeadList({ rows = 8 }: { rows?: number }): ReactElement {
  return (
    <div className="space-y-4" role="status" aria-label="Cargando leads">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="p-3 rounded border border-[var(--color-munya-sand)] bg-[var(--color-munya-surface)]/40 space-y-1.5"
          >
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-6 w-1/2" />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-8 w-20" />
      </div>
      <SkeletonTable rows={rows} cols={5} />
    </div>
  );
}

/**
 * Skeleton drawer (lateral) · header + 3 secciones.
 */
export function SkeletonDrawer(): ReactElement {
  return (
    <div className="space-y-4" role="status" aria-label="Cargando detalle">
      <div className="space-y-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-6 w-3/4" />
      </div>
      <SkeletonText lines={3} />
      <div className="space-y-1.5">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-1.5">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}
